import React, { useState } from "react";
import { todayString } from "../../utils/dateUtils";
import { formatDueDate, formatMinutes, initials } from "./taskUtils";
import useConfirm from "./ConfirmDialog";

const MAX_MINUTES = 24 * 60;

// Accepts "1h 30m", "1h", "45m", "90" (minutes) or "1.5h" / "1.5" with a
// decimal point (hours). Returns whole minutes, or null when unreadable.
export function parseDuration(input) {
  const text = String(input || "")
    .trim()
    .toLowerCase();
  if (!text) return null;
  const hm = /^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/.exec(text);
  if (hm && (hm[1] || hm[2])) {
    return Math.round(Number(hm[1] || 0) * 60 + Number(hm[2] || 0));
  }
  if (/^\d+\.\d+$/.test(text)) return Math.round(Number(text) * 60);
  if (/^\d+$/.test(text)) return Number(text);
  return null;
}

// "Time logged" block in the task pane: total, a quick entry form and the
// individual entries.
export default function TimeLogSection({ task, onLog, onRemove }) {
  const [duration, setDuration] = useState("");
  const [date, setDate] = useState(todayString());
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const minutes = parseDuration(duration);

  const submit = async (e) => {
    e.preventDefault();
    if (!minutes || minutes < 1 || minutes > MAX_MINUTES) {
      setFormError(
        "Enter a time between 1 minute and 24 hours, e.g. 1h 30m or 45m.",
      );
      return;
    }
    setSaving(true);
    setFormError("");
    const ok = await onLog({ minutes, date, note: note.trim() || undefined });
    setSaving(false);
    if (ok) {
      setDuration("");
      setNote("");
      setOpen(false);
    }
  };

  return (
    <div className="tk-section">
      <h3 className="tk-section-title">
        Time logged
        <span className="tk-time-total">
          {formatMinutes(task.minutesLogged)}
        </span>
        {!open && (
          <button
            type="button"
            className="tk-link-btn tk-time-add"
            onClick={() => setOpen(true)}
          >
            + Log time
          </button>
        )}
      </h3>

      {open && (
        <form className="tk-time-form" onSubmit={submit}>
          <input
            autoFocus
            className="tk-input"
            placeholder="e.g. 1h 30m"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            aria-label="Time spent"
          />
          <input
            type="date"
            className="tk-input"
            value={date}
            max={todayString()}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Date worked"
          />
          <input
            className="tk-input tk-time-note"
            placeholder="What did you work on? (optional)"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Note"
          />
          <div className="tk-time-actions">
            {minutes > 0 && minutes <= MAX_MINUTES && (
              <span className="tk-muted">= {formatMinutes(minutes)}</span>
            )}
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => {
                setOpen(false);
                setFormError("");
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary btn-sm"
              disabled={saving}
            >
              {saving ? "Saving…" : "Log time"}
            </button>
          </div>
          {formError && <p className="tk-error tk-time-error">{formError}</p>}
        </form>
      )}

      {task.timeLogs.length > 0 ? (
        <ul className="tk-time-list">
          {task.timeLogs.map((log) => (
            <li key={log.id}>
              <span className="tk-avatar tk-avatar-sm" title={log.user?.name}>
                {initials(log.user?.name)}
              </span>
              <span className="tk-time-mins">{formatMinutes(log.minutes)}</span>
              <span className="tk-time-meta">
                {log.user?.name} · {formatDueDate(log.date)}
                {log.note && (
                  <span className="tk-time-note-text"> — {log.note}</span>
                )}
              </span>
              {log.canDelete && (
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Remove this time entry?",
                      subject: `${formatMinutes(log.minutes)} · ${log.user?.name} · ${formatDueDate(log.date)}`,
                      message:
                        "It will no longer count towards this task or any report.",
                      confirmLabel: "Remove entry",
                    });
                    if (ok) onRemove(log.id);
                  }}
                  aria-label="Remove time entry"
                  title="Remove"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        !open && <p className="tk-muted">No time logged yet.</p>
      )}
      {confirmDialog}
    </div>
  );
}
